import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { fmtINR, calcExchange, EXCHANGE_DEFAULTS, EXCHANGE_PURITY_ORNAMENT, EXCHANGE_PURITY_RAW } from "../lib/constants";
import { Scale } from "lucide-react";

/**
 * Old-gold exchange — STAFF version.
 *
 * Customers pick a carat and we look the purity up. Staff instead type the
 * purity PERCENTAGE their testing machine reports (e.g. 90%, 85.5%), because
 * that is the real measured value — so there is no karat dropdown here.
 *
 *   fine weight = grams x purity%
 *   net rate/g  = (24K rate/g / 1.03) - (1500 / 10)
 *   value       = net rate/g x fine weight
 *
 * `onValue` receives the computed amount so the caller can offset it against
 * a sale while assisting a customer.
 */
export default function StaffExchangeCalc({ onValue, compact = false }) {
  const [grams, setGrams] = useState("");
  const [purity, setPurity] = useState("");
  const [rate24k, setRate24k] = useState(0);
  const [cfg, setCfg] = useState(EXCHANGE_DEFAULTS);

  useEffect(() => {
    api.get("/api/crm/exchange/config").then((r) => {
      setRate24k(r.data?.gold_24k || 0);
      setCfg({
        gstPercent: r.data?.gst_percent ?? EXCHANGE_DEFAULTS.gstPercent,
        deductionPer10g: r.data?.deduction_per_10g ?? EXCHANGE_DEFAULTS.deductionPer10g,
      });
    }).catch(() => {
      api.get("/api/rates/current").then((r) => setRate24k(r.data?.gold_24k || 0)).catch(() => {});
    });
  }, []);

  const calc = calcExchange(grams, purity, rate24k, cfg);
  const ready = +grams > 0 && +purity > 0 && rate24k > 0;

  return (
    <div className={compact ? "" : "bg-white border border-line p-5"} data-testid="staff-exchange-calc">
      <div className="flex items-center gap-2 mb-3">
        <Scale size={16} className="text-gold"/>
        <h2 className="font-display text-2xl text-maroon">Old Gold Exchange</h2>
      </div>

      {rate24k <= 0 && (
        <div className="text-xs text-maroon mb-3">Today's 24K rate hasn't been entered yet.</div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm">
          <span className="eyebrow text-[10px]">Weight (grams)</span>
          <input
            type="number" inputMode="decimal" step="0.001" placeholder="e.g. 23"
            className="w-full border border-line px-3 py-2 bg-pearl mt-1"
            value={grams} onChange={(e) => setGrams(e.target.value)}
            data-testid="staff-exch-grams"
          />
        </label>
        <label className="text-sm">
          <span className="eyebrow text-[10px]">Tested purity (%)</span>
          <input
            type="number" inputMode="decimal" step="0.01" min="0" max="100" placeholder="e.g. 90"
            className="w-full border border-line px-3 py-2 bg-pearl mt-1"
            value={purity} onChange={(e) => setPurity(e.target.value)}
            data-testid="staff-exch-purity"
          />
        </label>
      </div>

      {/* Quick-fill starting points — always overwrite with the machine reading.
          Only raw gold (bar/coin) reaches 100%; an ornament never does. */}
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <span className="text-[10px] text-ash uppercase tracking-widest">Quick fill:</span>
        {Object.entries(EXCHANGE_PURITY_ORNAMENT).map(([k, pct]) => (
          <button
            key={k} type="button" onClick={() => setPurity(String(pct))}
            className="text-[11px] border border-line px-2 py-0.5 hover:border-maroon hover:text-maroon"
            data-testid={`staff-exch-quick-${k}`}
          >
            {k}K = {pct}%
          </button>
        ))}
        <button
          type="button" onClick={() => setPurity(String(EXCHANGE_PURITY_RAW))}
          className="text-[11px] border border-gold text-gold-dark px-2 py-0.5 hover:border-maroon hover:text-maroon"
          data-testid="staff-exch-quick-raw"
        >
          Raw bar/coin = 100%
        </button>
      </div>
      <p className="text-[11px] text-ash mt-1">
        Always enter the reading from your testing machine. Ornaments are never 100%.
      </p>

      {ready && (
        <div className="mt-4 bg-maroon text-pearl p-4" data-testid="staff-exch-result">
          <div className="eyebrow text-gold-light text-[10px]">Exchange value</div>
          <div className="font-display text-4xl mt-1">₹{Intl.NumberFormat("en-IN").format(Math.round(calc.value))}</div>

          <div className="text-[11px] mt-3 text-pearl/80 space-y-1 border-t border-pearl/20 pt-2">
            <Line label={`Fine weight (${grams}g × ${purity}%)`} value={`${calc.fineWeight.toFixed(3)} g`}/>
            <Line label="24K rate (incl. GST)" value={`₹${Intl.NumberFormat("en-IN").format(rate24k)}/g`}/>
            <Line label={`÷ ${(1 + cfg.gstPercent / 100).toFixed(2)} (GST out)`} value={`₹${calc.rateExGst.toFixed(2)}/g`}/>
            <Line label={`Less ₹${Intl.NumberFormat("en-IN").format(cfg.deductionPer10g)}/10g`} value={`− ₹${calc.deductionPerGram.toFixed(0)}/g`}/>
            <div className="border-t border-pearl/20 pt-1 mt-1">
              <Line label="Net rate" value={`₹${calc.netRate.toFixed(2)}/g`} strong/>
            </div>
          </div>

          {onValue && (
            <button
              type="button"
              onClick={() => onValue(Math.round(calc.value), { grams: +grams, purity: +purity, ...calc })}
              className="btn-gold mt-4 w-full justify-center"
              data-testid="staff-exch-apply"
            >
              Apply to this sale
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const Line = ({ label, value, strong }) => (
  <div className={`flex justify-between ${strong ? "font-semibold text-gold-light" : ""}`}>
    <span>{label}</span><span>{value}</span>
  </div>
);
